import React, { useEffect } from "react";
import { toast } from "react-toastify";
import { Updater, useImmer } from "use-immer";

import { PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { IgButton, IgTooltip } from "@intelligems/ig-theme";
import { OrganizationDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import * as Sentry from "@sentry/react";

import { sleep } from "./index";

interface UploadFileProps {
  disabled?: boolean;
  renderKey: string;
  variant?: any;
  color?: any;
  organization: OrganizationDto;
  setIsLoading: (v: boolean) => void;
  isLoading: boolean;
  onFileUploadSuccess: (bucket: string, key: string) => void;
  children: any;
}

const s3Client = new S3Client({
  region: import.meta.env.VITE_AWS_REGION,
  credentials: {
    accessKeyId: import.meta.env.VITE_S3_ACCESS_KEY_ID,
    secretAccessKey: import.meta.env.VITE_S3_SECRET_ACCESS_KEY,
  },
});

export const UploadFile = ({
  disabled,
  renderKey,
  variant,
  organization,
  setIsLoading,
  isLoading,
  color,
  onFileUploadSuccess,
  children,
}: UploadFileProps) => {
  const [selectedFile, setSelectedFile] = useImmer<File | undefined>(undefined);

  const onFileUploadSubmit = async (file: File) => {
    setIsLoading(true);
    const date = new Date().getTime();

    const fileExtension = file.name!.substring(file.name!.lastIndexOf(".") + 1);

    const params = {
      Body: file,
      Bucket: import.meta.env.VITE_S3_BUCKET as string,
      Key: `user-uploads/${organization.shopId}/${date}/${renderKey}.${fileExtension}`,
    };

    try {
      const command = new PutObjectCommand(params);
      const resp = await s3Client.send(command);
      if (resp.$metadata.httpStatusCode === 200) {
        await sleep(500);
        onFileUploadSuccess(params.Bucket, params.Key);
      }
    } catch (err) {
      if (setIsLoading) setIsLoading(false);
      toast.error("Error, Unable to Upload File");
      Sentry.captureException(err);
      Sentry.captureMessage("Unable to upload file to s3");
    }
  };

  useEffect(() => {
    if (selectedFile) {
      void onFileUploadSubmit(selectedFile);
    }
  }, [selectedFile]);

  return (
    <Grid item>
      <IgTooltip
        title={disabled ? "Pause the experiment to make product changes" : ""}
      >
        <IgButton
          disabled={disabled}
          variant={variant || "outlined"}
          loading={isLoading}
          color={color || "secondary"}
          size={"large"}
          component={"label"}
        >
          {children}
          <input
            type="file"
            onChange={(e) => {
              if (e.target.files) {
                setSelectedFile(e.target.files[0]);
                setIsLoading(true);
              }
            }}
            hidden
          />
        </IgButton>
      </IgTooltip>
    </Grid>
  );
};
